const hydratePrimaryCardImage = image => {
  if (!image?.dataset.plPrimarySrc) return;
  if (image.dataset.plPrimarySrcset) image.srcset = image.dataset.plPrimarySrcset;
  image.src = image.dataset.plPrimarySrc;
  image.removeAttribute('data-pl-primary-src');
  image.removeAttribute('data-pl-primary-srcset');
};

if (!customElements.get('parana-product-carousel')) {
class ParanaProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.cards = [...this.querySelectorAll('[data-carousel-card]')];
    this.dots = this.querySelector('[data-carousel-dots]');
    this.viewport = this.querySelector('.pl-v6-product-carousel__viewport');
    this.index = 0;
    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => {
      this.activate(true);
      this.go(this.index - 1);
    });
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => {
      this.activate(true);
      this.go(this.index + 1);
    });
    this.handleScroll = () => {
      if (!this.usesNativeScroll() || !this.pages) return;
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        const pageWidth = Math.max(1, this.viewport.scrollWidth / this.pages);
        this.index = Math.max(0, Math.min(this.pages - 1, Math.round(this.viewport.scrollLeft / pageWidth)));
        this.updateDots();
      });
    };
    this.viewport?.addEventListener('scroll', this.handleScroll, { passive: true });
    const deferredPrimaryImages = [...this.querySelectorAll('[data-pl-primary-src]')];
    if (deferredPrimaryImages.length) {
      if ('IntersectionObserver' in window) {
        this.primaryImageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.querySelectorAll('[data-pl-primary-src]').forEach(hydratePrimaryCardImage);
            this.primaryImageObserver.unobserve(entry.target);
          });
        }, { root: this.viewport, rootMargin: '0px 35% 0px 35%' });
        [...new Set(deferredPrimaryImages.map(image => image.closest('[data-carousel-card]')).filter(Boolean))]
          .forEach(card => this.primaryImageObserver.observe(card));
      } else {
        deferredPrimaryImages.forEach(hydratePrimaryCardImage);
      }
    }
    this.queueSetup = () => {
      window.cancelAnimationFrame(this.setupFrame);
      this.setupFrame = window.requestAnimationFrame(() => this.setup());
    };
    window.addEventListener('resize', this.queueSetup, { passive: true });
    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      this.activationObserver = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.activate();
      }, { rootMargin: '200px 0px' });
      this.activationObserver.observe(this);
    } else {
      this.activate();
    }
  }
  activate(immediate = false) {
    if (this.activated) {
      if (immediate) this.setup();
      return;
    }
    this.activated = true;
    this.activationObserver?.disconnect();
    if (immediate) this.setup();
    else this.queueSetup();
  }
  disconnectedCallback() {
    this.activationObserver?.disconnect();
    this.primaryImageObserver?.disconnect();
    window.removeEventListener('resize', this.queueSetup);
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    window.cancelAnimationFrame(this.setupFrame);
    window.cancelAnimationFrame(this.scrollFrame);
  }
  usesNativeScroll() { return window.matchMedia('(max-width: 749px)').matches; }
  columns() {
    const desktopColumns = Math.max(1, Number(this.dataset.columns) || 4);
    if (window.innerWidth < 750) return Math.max(1, Number(this.dataset.mobileColumns) || 1);
    if (window.innerWidth < 990) return Math.max(1, Number(this.dataset.tabletColumns) || Math.min(desktopColumns, 2));
    if (window.innerWidth < 1200) return Math.max(1, Number(this.dataset.compactColumns) || Math.min(desktopColumns, 3));
    return desktopColumns;
  }
  setup() {
    const viewport = this.viewport;
    if (!viewport || !this.cards.length) return;
    const columns = this.columns();
    const layoutKey = `${window.innerWidth}|${columns}`;
    if (this.layoutKey === layoutKey) return;
    this.layoutKey = layoutKey;
    this.pageColumns = columns;
    this.pages = Math.max(1, Math.ceil(this.cards.length / columns));
    this.index = Math.min(this.index, this.pages - 1);
    if (this.renderedPages !== this.pages) {
      this.renderedPages = this.pages;
      this.dots.replaceChildren();
      for (let i = 0; i < this.pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button'; dot.setAttribute('aria-label', `Product page ${i + 1}`);
        dot.addEventListener('click', () => this.go(i)); this.dots.append(dot);
      }
    }
    this.go(this.index, false);
  }
  updateDots() {
    [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
  }
  go(index, smooth = true) {
    if (!this.pages) return;
    this.index = (index + this.pages) % this.pages;
    if (this.usesNativeScroll()) {
      this.track.style.transform = '';
      const target = this.cards[this.index * (this.pageColumns || 1)];
      if (target && (this.index !== 0 || smooth)) target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest', inline: 'center' });
    } else {
      this.viewport.scrollLeft = 0;
      const gap = Math.max(0, Number(this.dataset.gap) || 0);
      const offsetPercent = this.index * 100;
      const offsetGap = this.index * gap;
      this.track.style.transform = `translate3d(calc(-${offsetPercent}% - ${offsetGap}px),0,0)`;
    }
    this.updateDots();
  }
}
customElements.define('parana-product-carousel', ParanaProductCarousel);
}

const hydrateSecondaryCardImage = card => {
  const image = card?.querySelector('[data-pl-secondary-src]');
  if (!image) return;

  const media = image.closest('.pl-v6-product-card__media--has-secondary');
  const revealWhenReady = async () => {
    try {
      if (typeof image.decode === 'function') await image.decode();
    } catch (_) {}
    if (image.complete && image.naturalWidth > 1) media?.classList.add('is-secondary-ready');
  };

  image.addEventListener('load', revealWhenReady, { once: true });
  if (image.dataset.plSecondarySrcset) image.srcset = image.dataset.plSecondarySrcset;
  image.src = image.dataset.plSecondarySrc;
  image.removeAttribute('data-pl-secondary-src');
  image.removeAttribute('data-pl-secondary-srcset');
  if (image.complete) revealWhenReady();
};

document.addEventListener('pointerover', event => {
  if (event.pointerType === 'touch') return;
  hydrateSecondaryCardImage(event.target.closest('.pl-v6-product-card'));
}, { passive: true });

document.addEventListener('focusin', event => {
  hydrateSecondaryCardImage(event.target.closest('.pl-v6-product-card'));
});

if (!window.__plRailPrescription) {
  window.__plRailPrescription = true;

  let activeRailMenu = null;
  const closeRailMenu = () => { activeRailMenu?.remove(); activeRailMenu = null; };
  const closeRailPanel = panel => { closeRailMenu(); if (panel) panel.hidden = true; };
  const railVariant = (panel, power) => {
    const variants = JSON.parse(panel.dataset.variants || '[]');
    const powerIndex = Number(panel.dataset.powerIndex || 0);
    return variants.find(variant => variant.available && variant.options?.[powerIndex] === power);
  };
  const railCartSectionIds = () => [...document.querySelectorAll('cart-items-component')].map(component => component.dataset.sectionId).filter(Boolean);
  const refreshRailCart = async (sections = {}) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const cart = response.ok ? await response.json() : null;
    if (cart) {
      document.querySelectorAll('.pl-header__cart-count').forEach(node => { node.textContent = cart.item_count; node.hidden = cart.item_count < 1; });
      const event = new Event('shopify:cart:lines-update', { bubbles: true });
      event.action = 'add';
      event.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail: { didError: false, itemCount: cart.item_count, source: 'parana-product-rail', sections } });
      document.dispatchEvent(event);
    }
    return cart;
  };
  const openRailCart = () => {
    if (window.Shopify?.actions?.openCart) return window.Shopify.actions.openCart();
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') return drawer.open();
    document.querySelector('[aria-controls="cart-drawer"]')?.click();
  };

  const railPowerNumber = label => {
    const matches = String(label || '').replace(',', '.').match(/[+-]?\d+(?:\.\d+)?/g);
    return matches?.length ? Number(matches[matches.length - 1]) : null;
  };
  const railPowerOption = (item, select) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pl-rail-rx-menu__option ${item.number > 0 ? 'is-positive' : 'is-negative'}`;
    button.textContent = item.label;
    button.addEventListener('click', event => {
      event.stopPropagation();
      select.value = item.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      select.nextElementSibling.textContent = item.label;
      closeRailMenu();
    });
    return button;
  };
  const openRailPowerMenu = (select, trigger, panel) => {
    closeRailMenu();
    const variants = JSON.parse(panel.dataset.variants || '[]');
    const powerIndex = Number(panel.dataset.powerIndex || 0);
    const available = new Set(variants.filter(variant => variant.available).map(variant => variant.options?.[powerIndex]));
    const items = [...select.options].slice(1).filter(option => available.has(option.value)).map(option => ({ value: option.value, label: option.textContent.trim(), number: railPowerNumber(option.textContent) })).filter(item => item.number !== null);
    const zero = items.find(item => item.number === 0);
    const negative = items.filter(item => item.number < 0);
    const positive = items.filter(item => item.number > 0);
    const hasNegative = negative.length > 0;
    const hasPositive = positive.length > 0;
    const viewportWidth = window.innerWidth;
    const rect = trigger.getBoundingClientRect();
    const preferredWidth = hasNegative && hasPositive ? 300 : 210;
    const menuWidth = Math.min(preferredWidth, viewportWidth - 24);
    const menu = document.createElement('div');
    menu.className = `pl-rail-rx-menu${hasNegative !== hasPositive ? ' is-single' : ''}`;
    menu.style.setProperty('--rail-rx-rows', panel.dataset.dropdownRows || '5');
    if (zero) {
      const zeroButton = railPowerOption(zero, select);
      zeroButton.classList.add('pl-rail-rx-menu__zero');
      menu.append(zeroButton);
    }
    if (hasNegative || hasPositive) {
      const heads = document.createElement('div');
      heads.className = 'pl-rail-rx-menu__heads';
      if (hasNegative) { const head = document.createElement('strong'); head.textContent = panel.dataset.negativeLabel; heads.append(head); }
      if (hasPositive) { const head = document.createElement('strong'); head.className = 'is-positive'; head.textContent = panel.dataset.positiveLabel; heads.append(head); }
      menu.append(heads);
      const scroll = document.createElement('div');
      scroll.className = 'pl-rail-rx-menu__scroll';
      const rows = document.createElement('div');
      rows.className = 'pl-rail-rx-menu__rows';
      for (let index = 0; index < Math.max(negative.length, positive.length); index += 1) {
        if (hasNegative) rows.append(negative[index] ? railPowerOption(negative[index], select) : document.createElement('span'));
        if (hasPositive) rows.append(positive[index] ? railPowerOption(positive[index], select) : document.createElement('span'));
      }
      scroll.append(rows); menu.append(scroll);
    }
    menu.style.width = `${menuWidth}px`;
    menu.style.left = `${Math.max(12, Math.min(rect.left, viewportWidth - menuWidth - 12))}px`;
    menu.style.top = `${rect.bottom + 4}px`;
    document.body.append(menu);
    activeRailMenu = menu;
  };
  const prepareRailPanel = panel => {
    const values = (panel.dataset.powerValues || '').split('||').filter(Boolean);
    panel.querySelectorAll('[data-rail-power]').forEach(select => {
      if (select.dataset.customReady) return;
      values.forEach(value => select.add(new Option(value, value)));
      select.dataset.customReady = 'true';
      select.classList.add('pl-rail-rx-native');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pl-rail-rx-button';
      button.textContent = select.options[select.selectedIndex]?.textContent || '';
      button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); openRailPowerMenu(select, button, panel); });
      select.insertAdjacentElement('afterend', button);
    });
  };

  document.addEventListener('click', async event => {
    const card = event.target.closest('[data-card-url]');
    if (card && !event.target.closest('a, button, select, input, label, [data-rail-prescription]')) {
      window.location.href = card.dataset.cardUrl;
      return;
    }

    const buyNowButton = event.target.closest('[data-rail-buy-now]');
    if (buyNowButton) {
      event.preventDefault();
      const variantId = Number(buyNowButton.dataset.variantId);
      if (!variantId) return;
      const original = buyNowButton.textContent;
      buyNowButton.disabled = true;
      buyNowButton.textContent = '...';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: railCartSectionIds().join(',') })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this product.');
        await refreshRailCart(data.sections || {});
        openRailCart();
        buyNowButton.disabled = false;
        buyNowButton.textContent = original;
      } catch (errorValue) {
        console.error('Parana rail buy now error:', errorValue);
        buyNowButton.disabled = false;
        buyNowButton.textContent = original;
      }
      return;
    }

    const openButton = event.target.closest('[data-rail-prescription-open]');
    if (openButton) {
      event.preventDefault();
      const panel = openButton.closest('.pl-v6-product-card')?.querySelector('[data-rail-prescription]');
      document.querySelectorAll('[data-rail-prescription]:not([hidden])').forEach(other => { if (other !== panel) closeRailPanel(other); });
      if (panel) { panel.hidden = false; prepareRailPanel(panel); panel.querySelector('.pl-rail-rx-button')?.focus(); }
      return;
    }

    const closeButton = event.target.closest('[data-rail-prescription-close]');
    if (closeButton) { event.preventDefault(); closeRailPanel(closeButton.closest('[data-rail-prescription]')); return; }

    const addButton = event.target.closest('[data-rail-prescription-add]');
    if (!addButton) return;
    event.preventDefault();
    const panel = addButton.closest('[data-rail-prescription]');
    const selects = [...panel.querySelectorAll('[data-rail-power]')];
    const error = panel.querySelector('[data-rail-prescription-error]');
    const selected = selects.map(select => ({ select, variant: select.value ? railVariant(panel, select.value) : null }));
    const valid = selected.every(item => item.variant);
    if (error) error.hidden = valid;
    selects.forEach((select, index) => select.setAttribute('aria-invalid', String(!selected[index].variant)));
    if (!valid) { selected.find(item => !item.variant)?.select.focus(); return; }

    const grouped = new Map();
    selected.forEach((item, index) => {
      const key = String(item.variant.id);
      const eye = index === 0 ? 'Left eye' : 'Right eye';
      if (!grouped.has(key)) {
        const properties = { Prescription: item.select.value, Eye: eye };
        if (Math.abs(railPowerNumber(item.select.value)) >= 6) properties['_Made to order'] = 'true';
        grouped.set(key, { id: item.variant.id, quantity: 0, properties });
      }
      else grouped.get(key).properties.Eye += ` / ${eye}`;
      grouped.get(key).quantity += 1;
    });

    const original = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = addButton.dataset.loadingLabel || original;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [...grouped.values()], sections: railCartSectionIds().join(',') }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || panel.dataset.addError || 'Unable to add the selected powers.');
      await refreshRailCart(data.sections || {});
      closeRailPanel(panel);
      openRailCart();
    } catch (errorValue) {
      if (error) { error.textContent = errorValue.message; error.hidden = false; }
    } finally {
      addButton.disabled = false;
      addButton.textContent = original;
    }
  });
  document.addEventListener('pointerdown', event => {
    if (activeRailMenu && !activeRailMenu.contains(event.target) && !event.target.closest('.pl-rail-rx-button')) closeRailMenu();
  });
  window.addEventListener('resize', closeRailMenu);
  window.addEventListener('scroll', event => { if (!activeRailMenu?.contains(event.target)) closeRailMenu(); }, true);
}
